import React from "react";
import { useFocusTrap } from "../../hooks/useFocusTrap";
import type { SupportedAriaPopupRole } from "../../utils/SupportedAriaPopupRole";
import { isHTMLElement } from "../../utils/isHTMLElement";
interface UseDropdownButtonOptions {
    trapFocus: boolean;
    dropdownAriaRole: SupportedAriaPopupRole;
}
export function useDropdownButton(options: UseDropdownButtonOptions): DropdownButtonState {
    const [isOpen, setIsOpen] = React.useState(false);
    return useOpenControlledDropdownButton({ ...options, isOpen, setIsOpen });
}
interface UseOpenControlledDropdownButtonOptions extends UseDropdownButtonOptions {
    isOpen: boolean;
    setIsOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
}
export function useOpenControlledDropdownButton({ trapFocus, dropdownAriaRole, setIsOpen, isOpen }: UseOpenControlledDropdownButtonOptions): DropdownButtonState {
    const baseId = React.useId();
    const dropdownId = `dropdown-${baseId}`;
    const [buttonElement, setButtonElement] = React.useState<HTMLElement | null>(null);
    const [dropdownElement, setDropdownElement] = React.useState<HTMLElement | null>(null);
    useFocusTrap(dropdownElement, trapFocus);
    const closeDropdown = React.useCallback((options?: CloseDropdownOptions) => {
        setIsOpen(false);
        if (options?.moveFocusToButton) {
            buttonElement?.focus();
        }
    }, [setIsOpen, buttonElement]);
    const toggleDropdown = React.useCallback(() => {
        setIsOpen((prev) => !prev);
    }, [setIsOpen]);
    React.useEffect(() => {
        if (!isOpen || buttonElement === null || dropdownElement === null)
            return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeDropdown({ moveFocusToButton: true });
                event.preventDefault();
            }
        };
        const onFocusInOrPointerDown = (event: FocusEvent | PointerEvent) => {
            if (isHTMLElement(event.target) && !buttonElement.contains(event.target) && !dropdownElement.contains(event.target)) {
                closeDropdown();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("focusin", onFocusInOrPointerDown);
        window.addEventListener("pointerdown", onFocusInOrPointerDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("focusin", onFocusInOrPointerDown);
            window.removeEventListener("pointerdown", onFocusInOrPointerDown);
        };
    }, [isOpen, buttonElement, dropdownElement, closeDropdown]);
    const buttonAriaAttributes: ButtonAriaAttributes = {
        "aria-expanded": isOpen ? "true" : "false",
        "aria-haspopup": dropdownAriaRole,
        "aria-controls": isOpen ? dropdownId : undefined,
    };
    const dropdownAttributes: DropdownAttributes = {
        dropdownId,
        dropdownAriaAttributes: {
            role: dropdownAriaRole,
        },
    };
    return {
        isOpen,
        closeDropdown,
        toggleDropdown,
        buttonElement,
        setButtonRef: setButtonElement,
        buttonAriaAttributes,
        setDropdownRef: setDropdownElement,
        dropdownAttributes,
    };
}
export interface DropdownButtonState {
    isOpen: boolean;
    toggleDropdown: () => void;
    closeDropdown: (options?: CloseDropdownOptions) => void;
    buttonElement: HTMLElement | null;
    setButtonRef: (button: HTMLElement | null) => void;
    buttonAriaAttributes: ButtonAriaAttributes;
    setDropdownRef: (dropdown: HTMLElement | null) => void;
    dropdownAttributes: DropdownAttributes;
}
interface CloseDropdownOptions {
    moveFocusToButton?: boolean;
}
interface ButtonAriaAttributes {
    "aria-expanded": "true" | "false";
    "aria-haspopup": SupportedAriaPopupRole;
    "aria-controls": string | undefined;
}
interface DropdownAttributes {
    dropdownId: string;
    dropdownAriaAttributes: {
        role: SupportedAriaPopupRole;
    };
}
